/**
 * SharePreviewDialog Component
 * 
 * Modal dialog for customizing and previewing the share image.
 * Allows users to toggle theme, metadata visibility, and formatting options.
 */

import React, { useState, useRef, useEffect } from 'react';
// html2canvas removed from the export path to surface failures clearly
import { toPng } from 'html-to-image';
import ShareImageCard from './ShareImageCard';
import { hasMarkdownFormatting as detectMarkdown } from '../utils/markdown';

const SharePreviewDialog = ({ 
  isOpen, 
  onClose, 
  selectedText, 
  selectedMarkdown,
  hasMarkdown,
  articleTitle,
  articleUrl 
}) => {
  const [isDark, setIsDark] = useState(false);
  const [includeTitle, setIncludeTitle] = useState(true);
  const [includeUrl, setIncludeUrl] = useState(true);
  const [preserveFormatting, setPreserveFormatting] = useState(
    hasMarkdown || detectMarkdown(selectedMarkdown || '') || detectMarkdown(selectedText || '')
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [exportMode, setExportMode] = useState(false);
  
  const cardRef = useRef(null);
  
  // Update preserve formatting when hasMarkdown changes
  useEffect(() => {
    setPreserveFormatting(
      hasMarkdown || detectMarkdown(selectedMarkdown || '') || detectMarkdown(selectedText || '')
    );
  }, [hasMarkdown, selectedMarkdown, selectedText]);
  
  
  if (!isOpen) return null;
  
  // Don't show dialog if no text is selected
  if (!selectedText || selectedText.trim().length === 0) {
    return null;
  }
  
  // Share configuration
  const shareConfig = {
    selectedText,
    selectedMarkdown: selectedMarkdown && selectedMarkdown.trim().length > 0 ? selectedMarkdown : selectedText,
    preserveFormatting,
    articleTitle,
    articleUrl,
    includeTitle,
    includeUrl,
  };
  
  
  // Generate and download image
  const handleGenerateImage = async () => {
    if (!cardRef.current) return;
    
    setIsGenerating(true);
    
    try {
      // Wait a tick for layout stabilization
      await new Promise(resolve => setTimeout(resolve, 150));

      // Ensure fonts are loaded
      if (document.fonts) {
        await document.fonts.ready;
      }

      // Ensure images inside the card are fully loaded (e.g., logo)
      const waitForImages = (rootEl) => new Promise((resolve) => {
        const images = Array.from(rootEl.querySelectorAll('img'));
        if (images.length === 0) return resolve();

        let loaded = 0;
        const checkDone = () => {
          loaded += 1;
          if (loaded >= images.length) resolve();
        };

        images.forEach((img) => {
          if (img.complete && img.naturalWidth > 0) {
            checkDone();
          } else {
            img.addEventListener('load', checkDone, { once: true });
            img.addEventListener('error', checkDone, { once: true });
          }
        });
      });

      await waitForImages(cardRef.current);

      // Enable export-mode styling for perfect alignment in the capture
      setExportMode(true);
      // Wait one frame to ensure React applied the style
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      // Use html-to-image only; if it fails, let it fail for debugging clarity
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: undefined,
        filter: (node) => !(node.tagName === 'STYLE' || node.tagName === 'LINK'),
        style: { margin: 0 },
        skipFonts: true,
      });

      // Convert dataUrl to Canvas so we can trim bottom transparent rows
      const canvas = await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(c);
        };
        img.onerror = () => {
          // If conversion fails, create a 1x1 transparent canvas to avoid crashes
          const c = document.createElement('canvas');
          c.width = 1; c.height = 1; resolve(c);
        };
        img.src = dataUrl;
      });

      // Trim only bottom transparent rows to avoid extra whitespace without affecting width
      const trimBottomTransparent = (inputCanvas) => {
        const ctx = inputCanvas.getContext('2d');
        const { width, height } = inputCanvas;
        const imageData = ctx.getImageData(0, 0, width, height).data;

        let lastOpaqueRow = height - 1;
        outer: for (let y = height - 1; y >= 0; y -= 1) {
          const rowStart = y * width * 4;
          for (let x = 0; x < width; x += 1) {
            const alpha = imageData[rowStart + x * 4 + 3];
            if (alpha !== 0) {
              lastOpaqueRow = y;
              break outer;
            }
          }
        }

        const newHeight = Math.max(1, lastOpaqueRow + 1);
        if (newHeight === height) return inputCanvas;

        const out = document.createElement('canvas');
        out.width = width;
        out.height = newHeight;
        const outCtx = out.getContext('2d');
        outCtx.drawImage(inputCanvas, 0, 0);
        return out;
      };

      const outputCanvas = trimBottomTransparent(canvas);
      
      // Debug canvas
      console.log('Canvas created:', canvas.width, 'x', canvas.height);
      
      // Convert to blob and download
      const exportCanvas = outputCanvas || canvas;
      exportCanvas.toBlob((blob) => {
        if (!blob) {
          console.error('Failed to create blob from canvas');
          alert('Failed to generate image. Please try again.');
          return;
        }
        
        console.log('Blob created:', blob.size, 'bytes');
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `read-it-never-quote-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Close dialog after successful generation
        onClose();
      }, 'image/png', 0.95);
      
    } catch (error) {
      console.error('Error generating image:', error);
      alert('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
      setExportMode(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Share as Image
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Preview */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Preview
              </h3>
              
              <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg flex items-center justify-center min-h-96">
                <ShareImageCard 
                  ref={cardRef}
                  config={shareConfig}
                  isDark={isDark}
                  exportMode={exportMode}
                />
              </div>
            </div>
            
            {/* Customization Options */}
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Customize
              </h3>
              
              {/* Theme Toggle */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Theme
                </label>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setIsDark(false)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      !isDark 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Light
                  </button>
                  <button
                    onClick={() => setIsDark(true)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isDark 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Dark
                  </button>
                </div>
              </div>
              
              {/* Metadata Options */}
              <div className="space-y-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Metadata
                </label>
                
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={includeTitle}
                      onChange={(e) => setIncludeTitle(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Include article title
                    </span>
                  </label>
                  
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={includeUrl}
                      onChange={(e) => setIncludeUrl(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Include article URL
                    </span>
                  </label>
                </div>
              </div>
              
              {/* Formatting Options */}
              {hasMarkdown && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Formatting
                  </label>
                  
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={preserveFormatting}
                      onChange={(e) => setPreserveFormatting(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Preserve markdown formatting
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">
                        ✨ Bold, italic, links, etc.
                      </span>
                    </span>
                  </label>
                </div>
              )}
              
              {/* Selected Text Preview */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Selected Text
                </label>
                <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-md text-sm text-gray-700 dark:text-gray-300 max-h-32 overflow-y-auto">
                  {selectedText}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleGenerateImage}
            disabled={isGenerating}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center space-x-2"
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Download Image</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SharePreviewDialog;